import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold tracking-tight mt-6 mb-2">{children}</h2>
}

function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-medium mt-4 mb-1.5">{children}</h3>
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground leading-relaxed mb-2">{children}</p>
}

function DocTable({
  columns,
  rows,
}: {
  columns: string[]
  rows: React.ReactNode[][]
}) {
  return (
    <div className="rounded-md border mb-3">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c} className="h-8 px-3 text-xs">
                {c}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => (
                <TableCell key={j} className="p-3 text-xs align-top">
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * In-app documentation for the Savings Simulator, mirroring docs/savings-simulator.md.
 * Keep these two in sync when the simulator's behaviour changes.
 */
export function SimulatorHelpDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Savings Simulator</DialogTitle>
          <DialogDescription>
            How the simulator works, what the charts and numbers mean, and where the price data
            comes from.
          </DialogDescription>
        </DialogHeader>

        <div>
          <P>
            The Savings Simulator models how much EV charging costs could be reduced by shifting
            load into cheaper Day-Ahead or Intraday Auction price windows, compared to either a
            fixed flat-rate tariff or immediate (unoptimised) charging.
          </P>
          <P>It has two perspectives:</P>
          <ul className="list-disc pl-5 text-sm text-muted-foreground leading-relaxed mb-2 space-y-1">
            <li>
              <span className="font-medium text-foreground">End User</span> — what the end
              customer pays: flat rate + grid offset vs. optimised market cost + grid offset.
            </li>
            <li>
              <span className="font-medium text-foreground">Trading Desk</span> — what the desk
              trades at: raw market price only, no grid offset, and no flat rate at all.
            </li>
          </ul>

          <H2>How the optimiser works</H2>
          <P>For each charging session the simulator runs a greedy slot scheduler:</P>
          <ol className="list-decimal pl-5 text-sm text-muted-foreground leading-relaxed mb-2 space-y-1">
            <li>
              Find all 15-minute price slots (of whichever curve is being optimised against) that
              fall within the session&apos;s plug-in window (plug-in time → scheduled departure or
              unplug time).
            </li>
            <li>
              Sort slots cheapest-first. For the End User perspective this is market price + grid
              offset; for the Trading Desk perspective it&apos;s raw market price only.
            </li>
            <li>
              Fill slots in that order until the required energy (
              <code className="text-xs bg-muted px-1 rounded">
                (battery capacity × (target SoC − start SoC)) / 100
              </code>
              ) is reached.
            </li>
            <li>
              Max energy per slot ={' '}
              <code className="text-xs bg-muted px-1 rounded">
                max charge power (kW) × 0.25 h × efficiency
              </code>
              .
            </li>
          </ol>
          <P>The result is the optimal cost — what the session would have cost if perfectly scheduled.</P>

          <H2>Parameters</H2>
          <DocTable
            columns={['Parameter', 'Default', 'Effect']}
            rows={[
              [
                'Flat Rate (€/kWh)',
                '0.25',
                "End-customer's fixed tariff. Used only for the End User savings comparison.",
              ],
              [
                'Grid Offset (€/kWh)',
                '0.10',
                'Grid fees, taxes, and levies added on top of every market price slot. Applied only in the End User perspective — the Trading Desk perspective trades at raw market price only and ignores it entirely.',
              ],
              [
                'Efficiency (%)',
                '90%',
                'Charger round-trip efficiency. Scales max kWh/slot. Lower values require more slots to reach the target SoC.',
              ],
            ]}
          />

          <H2>What the chart shows</H2>
          <P>The Price Curve chart (day view) shows several layers:</P>
          <DocTable
            columns={['Layer', 'Colour', 'Meaning']}
            rows={[
              [
                'Price lines',
                'Blue (DA), purple (ID-Cfull), sky blue (Cross-Market)',
                'Market prices in ct/kWh for the visible window. The Cross-Market line is the per-15-min min(DA, ID-Cfull) blend.',
              ],
              [
                'Actual charging',
                'Gray bars',
                "Real energy delivered each 15-min slot, derived from chargingPhases reported by the charger. Only shown when phase data is available.",
              ],
              [
                'Optimised',
                'Warm/cool gradient bars',
                'Simulated optimal schedule: energy placed in the cheapest DA slots. Height = kWh, colour = relative load.',
              ],
            ]}
          />
          <P>
            Background shading: green = DA price below flat rate (cheap); red = DA price above
            flat rate (expensive). Clicking a row in the session table selects that session
            (multi-select supported) and marks its plug-in/departure window with dashed lines.
          </P>

          <H2>Summary sections</H2>
          <H3>End User Perspective — Flat rate vs. optimised price</H3>
          <P>Answers: how much does smart scheduling save vs. a fixed tariff?</P>
          <ul className="list-disc pl-5 text-sm text-muted-foreground leading-relaxed mb-2 space-y-1">
            <li>DA Total Savings = Σ (flat cost − optimal cost) across all sessions.</li>
            <li>DA Avg Savings % = mean of (savings ÷ flat cost) per session.</li>
          </ul>
          <P>
            When an ID-Cfull price series is loaded, it runs the same optimiser independently and
            its savings are reported separately. The simulator only ever loads ID-Cfull from a
            price file — any other intraday columns (ID-A, ID-Cmin, ID-Cmax, ID-C1, ID-C3) are
            ignored.
          </P>

          <H3>Trading Desk Perspective — Optimised vs. Immediate Charge</H3>
          <P>
            Answers: how much does the trading desk save by scheduling into cheap slots vs.
            charging immediately after plug-in? Trades at raw market price only — no grid fees,
            taxes, levies, or flat rate involved on either side.
          </P>
          <ul className="list-disc pl-5 text-sm text-muted-foreground leading-relaxed mb-2 space-y-1">
            <li>
              TD Baseline = cost if the session had filled slots chronologically from plug-in,
              computed on the Day-Ahead curve. This one baseline is shared by all three scenarios
              below.
            </li>
            <li>TD Savings (per scenario) = TD Baseline − that scenario&apos;s optimal cost.</li>
          </ul>
          <P>
            Because all scenarios share the same baseline, only the optimal cost varies between
            them — the scenario that finds the cheapest slots always shows the highest savings.
          </P>

          <H2>Optimized scenarios (Trading Desk)</H2>
          <P>The Trading Desk perspective runs the same charging demand against three price curves:</P>
          <DocTable
            columns={['Scenario', 'Price curve', 'Colour']}
            rows={[
              ['Optimized DA', 'Day-Ahead', 'Amber'],
              [
                'Optimized IDCFull',
                'Intraday Continuous, full-session average (ID-Cfull)',
                'Purple',
              ],
              [
                'Optimized Cross-Market',
                'Per-15-min min(DA, ID-Cfull) — whichever market is cheaper at each delivery period',
                'Sky blue',
              ],
            ]}
          />
          <P>
            Cross-Market is a per-slot blend, not a two-stage re-trade: the two curves&apos;
            timestamps are unioned and each timestamp keeps the lower of the two prices. This
            guarantees Cross-Market&apos;s optimal cost is always ≤ min(DA optimal cost, IDCFull
            optimal cost). A scenario is hidden wherever it has no data (e.g. no ID-Cfull column in
            the loaded price file).
          </P>

          <H2>Price data sources</H2>
          <DocTable
            columns={['Source', 'Coverage', 'How to use']}
            rows={[
              ['Bundled EPEX Spot CSV', '2025–2026 DA + ID-Cfull', 'Loaded automatically'],
              ['SMARD.de CSV upload', 'Any range', 'Upload via "DA Prices → Upload CSV"'],
              [
                'EPEX IDA CSV upload',
                'Any range',
                'Upload via "IDA Prices → Upload CSV" (only ID-Cfull is used; other columns are ignored)',
              ],
            ]}
          />
          <P>DA prices from SMARD and EPEX are merged; uploaded data takes precedence over bundled data on overlap.</P>

          <H2>Pool eligibility</H2>
          <P>
            Only pools with <code className="text-xs bg-muted px-1 rounded">controlConfig.purpose</code>{' '}
            = <code className="text-xs bg-muted px-1 rounded">"intraday"</code> or{' '}
            <code className="text-xs bg-muted px-1 rounded">"intraday_cloud"</code> and at least one
            system are shown. These are the pools where flexible charging schedules are meaningful.
          </P>
        </div>
      </DialogContent>
    </Dialog>
  )
}
